<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { NTabs, NTabPane, NButton, NTag, useMessage } from 'naive-ui';
import { Receipt, Download, RefreshCw } from 'lucide-vue-next';
import { billingApi } from '@/services/api';
import type { InvoiceItem } from '@/types/models';

const { t } = useI18n();
const activeTab = ref<'TEAM' | 'INDIVIDUAL'>('TEAM');
const invoices = ref<InvoiceItem[]>([]);
const isLoading = ref(false);
const naiveMsg = useMessage();

const loadInvoices = async () => {
  isLoading.value = true;
  try {
    const list = await billingApi.getInvoices();
    if (list) {
      invoices.value = list;
    }
  } catch (err: any) {
    naiveMsg.error('Failed to load invoice records');
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadInvoices();
});

const tenantInvoices = computed(() => {
  return invoices.value.filter(i => i.payerScope === 'TEAM');
});

const individualInvoices = computed(() => {
  return invoices.value.filter(i => i.payerScope === 'INDIVIDUAL');
});

const getStatusTagType = (status: string) => {
  switch (status) {
    case 'PAID': return 'success';
    case 'OPEN': return 'warning';
    case 'VOID': return 'error';
    default: return 'default';
  }
};
</script>

<template>
  <div class="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-800/80">
      <div>
        <h3 class="text-sm font-extrabold uppercase tracking-wider text-slate-100 flex items-center gap-2">
          <Receipt class="w-4 h-4 text-sky-400" />
          {{ t('invoices.title') }}
        </h3>
        <p class="text-xs text-slate-400 mt-0.5">
          {{ t('invoices.subtitle') }}
        </p>
      </div>

      <NButton size="small" secondary @click="loadInvoices" :loading="isLoading" class="font-semibold text-xs">
        <template #icon><RefreshCw class="w-3.5 h-3.5" /></template>
        {{ t('invoices.refreshBtn') }}
      </NButton>
    </div>

    <!-- Naive UI n-tabs View with Tenant Payments & Individual Payments -->
    <NTabs v-model:value="activeTab" type="segment" animated class="custom-invoice-tabs">
      <!-- 1. Tenant Payments Tab -->
      <NTabPane name="TEAM" :tab="t('invoices.tabTenant')">
        <div class="pt-3">
          <div class="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950">
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left border-collapse">
                <thead>
                  <tr class="border-b border-slate-800/80 bg-slate-950 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    <th class="py-3 px-3">{{ t('invoices.colInvoiceNum') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colDate') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colDescription') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colBilledOrg') }}</th>
                    <th class="py-3 px-3 text-right">{{ t('invoices.colAmount') }}</th>
                    <th class="py-3 px-3 text-center">{{ t('invoices.colStatus') }}</th>
                    <th class="py-3 px-3 text-right">{{ t('invoices.colDownload') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-800/60">
                  <tr v-if="tenantInvoices.length === 0">
                    <td colspan="7" class="py-8 text-center text-slate-500 italic">
                      {{ t('invoices.noTenantInvoices') }}
                    </td>
                  </tr>
                  <tr
                    v-for="inv in tenantInvoices"
                    :key="inv.id"
                    class="hover:bg-slate-900/60 transition"
                  >
                    <td class="py-3 px-3 font-mono font-bold text-sky-400">
                      {{ inv.invoiceNumber }}
                    </td>
                    <td class="py-3 px-3 font-mono text-slate-400">
                      {{ inv.date }}
                    </td>
                    <td class="py-3 px-3 font-bold text-slate-200">
                      {{ inv.serviceName }}
                    </td>
                    <td class="py-3 px-3 text-slate-400">
                      {{ inv.payerName }}
                    </td>
                    <td class="py-3 px-3 text-right font-mono font-bold text-emerald-400">
                      ${{ inv.amount.toFixed(2) }}
                    </td>
                    <td class="py-3 px-3 text-center">
                      <NTag size="small" :type="getStatusTagType(inv.status)" :bordered="false">
                        {{ inv.status }}
                      </NTag>
                    </td>
                    <td class="py-3 px-3 text-right">
                      <a
                        :href="inv.pdfUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 text-[11px] font-bold transition border border-sky-500/30"
                      >
                        <Download class="w-3.5 h-3.5" />
                        {{ t('invoices.pdfBtn') }}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </NTabPane>

      <!-- 2. Individual Payments Tab -->
      <NTabPane name="INDIVIDUAL" :tab="t('invoices.tabIndividual')">
        <div class="pt-3">
          <div class="border border-slate-800 rounded-2xl overflow-hidden bg-slate-950">
            <div class="overflow-x-auto">
              <table class="w-full text-xs text-left border-collapse">
                <thead>
                  <tr class="border-b border-slate-800/80 bg-slate-950 text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                    <th class="py-3 px-3">{{ t('invoices.colInvoiceNum') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colDate') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colAthletePayer') }}</th>
                    <th class="py-3 px-3">{{ t('invoices.colService') }}</th>
                    <th class="py-3 px-3 text-right">{{ t('invoices.colAmount') }}</th>
                    <th class="py-3 px-3 text-center">{{ t('invoices.colStatus') }}</th>
                    <th class="py-3 px-3 text-right">{{ t('invoices.colDownload') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-800/60">
                  <tr v-if="individualInvoices.length === 0">
                    <td colspan="7" class="py-8 text-center text-slate-500 italic">
                      {{ t('invoices.noIndividualInvoices') }}
                    </td>
                  </tr>
                  <tr
                    v-for="inv in individualInvoices"
                    :key="inv.id"
                    class="hover:bg-slate-900/60 transition"
                  >
                    <td class="py-3 px-3 font-mono font-bold text-emerald-400">
                      {{ inv.invoiceNumber }}
                    </td>
                    <td class="py-3 px-3 font-mono text-slate-400">
                      {{ inv.date }}
                    </td>
                    <td class="py-3 px-3 font-bold text-slate-200">
                      {{ inv.payerName }}
                    </td>
                    <td class="py-3 px-3 text-slate-300">
                      {{ inv.serviceName }}
                    </td>
                    <td class="py-3 px-3 text-right font-mono font-bold text-slate-100">
                      ${{ inv.amount.toFixed(2) }}
                    </td>
                    <td class="py-3 px-3 text-center">
                      <NTag size="small" :type="getStatusTagType(inv.status)" :bordered="false">
                        {{ inv.status }}
                      </NTag>
                    </td>
                    <td class="py-3 px-3 text-right">
                      <a
                        :href="inv.pdfUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-[11px] font-bold transition border border-emerald-500/30"
                      >
                        <Download class="w-3.5 h-3.5" />
                        {{ t('invoices.pdfBtn') }}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </NTabPane>
    </NTabs>
  </div>
</template>
